import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";
import { pdf } from "@react-pdf/renderer";
import emailjs from "emailjs-com";
import toast from "react-hot-toast";
import moment from "moment";
import { fetchPartsApi } from "../components/Catalogue/Api/partsApi";
import { ReturnProductPdf } from "../components/ReturnProductPdf";
import { dextPolicyObject } from "../constant/dexPolicyObject";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import AsyncAutocomplete from "./AsyncAutocomplete";
import { Info } from "@mui/icons-material";

// ── Constants ────────────────────────────────────────────────────────────────

const TAB_LABELS = {
  recall: "Ανακλήσεις",
  normal: "Επιστροφές 15 ημερών",
  overDue: "Ληξιπρόθεσμες επιστροφές",
  costly: "Φάρμακα άνω €40",
  others: "Μη Ενεργά Φάρμακα",
};

const TAB_TYPE_CODES = {
  recall: "AN",
  normal: "KE",
  overDue: "LE",
  others: "OT",
  costly: "EF",
};

const COSTLY_MIN_PRICE = 40;

const NAV_TABS = ["recall", "normal", "overDue", "others"];

const NAV_TAB_LABELS = {
  recall: "Ανακλήσεις",
  normal: "Επιστροφές 15 ημερών",
  overDue: "Ληξιπρόθεσμες επιστροφές",
  costly: "Φάρμακα άνω €40",
  others: "Μη Ενεργά Φάρμακα",
};

const INVOICE_LOOKBACK_DAYS = 15;

const TABLE_HEADERS_BASE = [
  "IV",
  "CODE",
  "Description",
  "Return Policy",
  "Exp. Date",
  "Lot No.",
  "QTY",
  "Vendor Agreement",
];

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Evaluate whether a product's expiry date satisfies its return-policy code.
 * Extracted from the inline switch in handleAddItem — identical logic preserved.
 */
function evaluatePolicyPermission(policyCode, expiryMoment) {
  const today = moment();

  // Collapse the repeated "NM" pattern into a lookup
  const monthThresholds = { "1M": 1, "3M": 3, "4M": 4, "6M": 6 };
  if (monthThresholds[policyCode] !== undefined) {
    return expiryMoment.isSameOrAfter(
      today.clone().add(monthThresholds[policyCode], "months"),
    );
  }

  switch (policyCode) {
    case "6M/exp":
    case "6M/EXP":
      return true;

    case "exp":
    case "EXP":
      return expiryMoment.isBefore(today);

    case "NONE":
    case "NULL":
    case "Special":
      return false;

    default:
      return false;
  }
}

/**
 * Validates whether a product qualifies for the "costly" (Φάρμακα άνω €40) return tab.
 * Returns { valid: true } or { valid: false, reasons: string[] }.
 */
function validateCostlyProduct(product) {
  const reasons = [];

  // Must be over €40 excluding VAT
  const price = Number(product?.WSPLPRICE || 0);
  if (price <= COSTLY_MIN_PRICE) {
    reasons.push(
      `Η τιμή του προϊόντος (€${price.toFixed(2)}) πρέπει να είναι άνω των €${COSTLY_MIN_PRICE} (χωρίς ΦΠΑ).`,
    );
  }

  // Must NOT be refrigerated (DEXT_FRIDGE === "1" means refrigerated)
  if (product?.DEXT_FRIDGE === "1") {
    reasons.push(
      "Το προϊόν είναι ψυγείου και δεν μπορεί να επιστραφεί μέσω αυτής της φόρμας.",
    );
  }

  // Must have a pharma code (SPEC14 must be non-empty)
  if (!product?.SPEC14) {
    reasons.push("Το προϊόν δεν διαθέτει φαρμακευτικό κωδικό (Pharma Code).");
  }

  return reasons.length === 0
    ? { valid: true, reasons: [] }
    : { valid: false, reasons };
}

/**
 * Reusable row: Qty / Lot No. / Expiry Date / Add button.
 * Renders optional `before` elements at the start of the row.
 */
function ProductInputFields({
  selectedProducts,
  setSelectedProducts,
  onAdd,
  before,
}) {
  const updateField = (field, raw) => {
    const value = field === "qty" ? +raw : raw;
    setSelectedProducts((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="flex items-center w-full gap-2 mt-3">
      {before}
      <TextField
        size="small"
        type="number"
        placeholder="Qty"
        value={selectedProducts.qty || ""}
        onChange={(e) => updateField("qty", e.target.value)}
      />
      <TextField
        size="small"
        placeholder="Lot no."
        value={selectedProducts.lotNo || ""}
        onChange={(e) => updateField("lotNo", e.target.value)}
      />
      <TextField
        size="small"
        type="date"
        placeholder="Expiry date"
        value={selectedProducts.currentExpiry || ""}
        onChange={(e) => updateField("currentExpiry", e.target.value)}
      />
      <Button variant="contained" onClick={onAdd}>
        Add
      </Button>
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────

const ReturnProduct = ({ isVendorName }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const [productData, setProductData] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState({});
  const [searchValue, setSearchValue] = useState("");
  const [searchedOverdueProduct, setSearchedOverdueProduct] = useState(null);
  const [searchedNormalProduct, setSearchedNormalProduct] = useState(null);
  const [allInvoices, setAllInvoices] = useState([]);
  const [allReturnProducts, setAllReturnProducts] = useState([]);
  const [boxNumber, setBoxNumber] = useState("");
  const [importerName, setImporterName] = useState("");
  const [importerSignatureURL, setImporterSignatureURL] = useState("");
  const [isLoadingPolicy, setIsLoadingPolicy] = useState(false);
  const [isFetchingInvoice, setIsFetchingInvoice] = useState(false);
  const [currentTab, setCurrentTab] = useState("normal");
  const [costlyConfirmOpen, setCostlyConfirmOpen] = useState(false);
  const [costlyPendingProduct, setCostlyPendingProduct] = useState(null);

  const userFullId = localStorage.getItem("userId");
  const userDesc = localStorage.getItem("userDesc");
  const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");

  // ── Derived booleans (readability, no logic change) ──────────────────────
  const isOthersTab = currentTab === "others";
  const isNormalTab = currentTab === "normal";
  const isRecallTab = currentTab === "recall";
  const isOverDueTab = currentTab === "overDue";
  const isCostlyTab = currentTab === "costly";
  const showNavButtons = userFullId === "S9999" || isVendorName == "";
  const showAddSection =
    searchedOverdueProduct || searchedNormalProduct || isOthersTab;

  const tableHeaders = isOthersTab
    ? [...TABLE_HEADERS_BASE, "Comment"]
    : isCostlyTab
      ? TABLE_HEADERS_BASE.filter((item) => item !== "Vendor Agreement")
      : TABLE_HEADERS_BASE;

  console.log(params.get("type"), "params");

  // ── Effects ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (params.get("type")) {
      setCurrentTab(params.get("type"));
    }
  }, [params]);

  useEffect(() => {
    if (userFullId && userFullId.includes("V")) {
      navigate("/app");
    }
  }, [userFullId]);

  // ── API calls ────────────────────────────────────────────────────────────
  const fetchAllInvoices = async (PARTNAME) => {
    try {
      setIsFetchingInvoice(true);
      const today = moment().format("YYYY-MM-DD");
      const fifteenDaysAgo = moment()
        .subtract(INVOICE_LOOKBACK_DAYS, "days")
        .format("YYYY-MM-DD");

      const salesReportUrl = `${
        process.env.REACT_APP_API_URL
      }/get-sales?id=${encodeURIComponent(
        userDesc,
      )}&PARTCODE=${PARTNAME}&str=${fifteenDaysAgo}&end=${today}`;

      const response = await axios.get(salesReportUrl);
      const filteredData =
        response?.data?.length > 0 &&
        response?.data.filter(
          (item) => item?.total_qty && Number(item?.total_qty || 0) > 0,
        );

      if (filteredData?.length > 0) {
        setAllInvoices(filteredData);
      } else {
        setAllInvoices([]);
        window.alert(
          "Δεν υπάρχουν τιμολόγια των τελευταίων 15 ημερών με το προϊόν που επιλέξατε",
        );
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
    } finally {
      setIsFetchingInvoice(false);
    }
  };

  const fetchReturnPolicy = async (type) => {
    try {
      setIsLoadingPolicy(true);
      setSearchedNormalProduct(null);
      setSearchedOverdueProduct(null);

      if (type === "overDue" || type === "recall" || type === "costly") {
        // For costly tab, validate product eligibility before proceeding
        if (type === "costly") {
          const { valid, reasons } = validateCostlyProduct(productData);
          if (!valid) {
            alert(
              "Το προϊόν δεν πληροί τις προϋποθέσεις επιστροφής:\n\n" +
                reasons.join("\n"),
            );
            return;
          }
          // Product is eligible — ask user to confirm purchase was < 3 months ago
          setCostlyPendingProduct(productData);
          setCostlyConfirmOpen(true);
          return; // Don't set searchedOverdueProduct yet; wait for confirmation
        }
        setSearchedOverdueProduct(productData);
        setSearchedNormalProduct(null);
      } else if (type === "normal") {
        setSearchedNormalProduct(productData);
        setSearchedOverdueProduct(null);
        fetchAllInvoices(productData?.PARTNAME);
      } else if (type === "others") {
        setSearchedNormalProduct(productData);
        setSearchedOverdueProduct(null);
      }
    } catch (error) {
      console.error("Error fetching return policy:", error);
    } finally {
      setIsLoadingPolicy(false);
    }
  };

  const sendEmail = async (file) => {
    const templateParams = {
      file,
      from_name: userDetails.NAME,
      from_email: "rakesh@spirehubs.com",
      to_email: "rakesh@spirehubs.com",
      message: `<div><h1>Hello, ${userDetails.NAME}!</h1><p>This is a message with HTML content.</p></div>`,
    };
    try {
      await emailjs.send(
        "service_qcjor0n",
        "template_a9i1wy1",
        templateParams,
        "Uf1hsQIllFwhT1YhB",
      );
      console.log("Email successfully sent!");
    } catch (error) {
      console.error("Error sending email:", error);
    }
  };

  const handleSignatureUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImporterSignatureURL(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // ── Form submission ──────────────────────────────────────────────────────
  const handleSubmitForm = async () => {
    const invalidAgreements = allReturnProducts.filter(
      (item) => !item.periodPermitted && !item.agreement,
    );

    if (invalidAgreements.length > 0 && currentTab !== "costly") {
      alert(
        "Σύμφωνα με την πολιτική επιστροφών, το προϊόν δεν επιστρέφεται. Με έγκριση Αντιπροσώπου, συμπληρώστε το Special Agreement. Η επιστροφή ισχύει μόνο με υπογραφή και σφραγίδα στο έντυπο.",
      );
      return;
    }

    if(!window.confirm("Σε περίπτωση που, για οποιονδήποτε λόγο, η επιστροφή δεν πληροί τους όρους, τα προϊόντα θα επιστρέφονται πίσω στο φαρμακείο.")){
      return;
    }

    // Replaced if/else chain with lookup — "LE" is the fallback (same as original default)
    const type = TAB_TYPE_CODES[currentTab] || "LE";

    try {
      const otherInfoPayload = {
        importerName,
        importerSignatureURL,
        customer: { userFullId, name: userDetails.FIRM },
        type,
        currentTab,
      };
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/return-products`,
        {
          user_id: userFullId,
          products: allReturnProducts,
          type,
          otherInfo: JSON.stringify(otherInfoPayload),
        },
      );
      const otherInfo = {
        importerName,
        importerSignatureURL,
        serialNo: response?.data?.product_return_id,
        customer: { userFullId, name: userDetails.FIRM },
        type,
        currentTab,
      };
      const pdfBlob = await pdf(
        <ReturnProductPdf
          allReturnProducts={allReturnProducts}
          otherInfo={otherInfo}
        />,
      ).toBlob();
      window.open(URL.createObjectURL(pdfBlob), "_blank");
      window.location.reload();
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Server Error");
    }
  };

  // ── Add / Delete items ───────────────────────────────────────────────────
  const handleAddItem = () => {
    const isAlreadyAdded = allReturnProducts.some(
      (item) => item.PARTNAME === selectedProducts.PARTNAME,
    );

    if (isAlreadyAdded) {
      alert("This Part is already added");
      return;
    }

    if (allReturnProducts?.length >= 10) {
      alert("You can not add more than 10 products in a single return form");
      return;
    }

    if (
      !isOthersTab &&
      (!selectedProducts?.qty ||
        !selectedProducts?.lotNo ||
        !selectedProducts?.currentExpiry)
    ) {
      alert("Please fill all the field");
      return;
    }

    const searchedProduct = searchedOverdueProduct || searchedNormalProduct;
    const allProductKeys = { ...searchedProduct, ...selectedProducts };

    console.log(allProductKeys, "allProductKeys");

    const currentExpiryDate = allProductKeys.currentExpiry
      ? moment(allProductKeys.currentExpiry, "YYYY-MM-DD")
      : null;
    const policyCode = allProductKeys.DEXT_CSPOLICYCODE;

    console.log(policyCode, "policyCode");

    // Preserve original logic exactly:
    // Default = true when searchedNormalProduct exists OR recall OR others
    let isPeriodPermitted =
      searchedNormalProduct || isRecallTab || isOthersTab ? true : false;

    // Only evaluate policy when we have expiry + policyCode AND
    // product came from overdue search (not normal) AND not recall tab
    if (
      currentExpiryDate &&
      policyCode &&
      !searchedNormalProduct &&
      !isRecallTab
    ) {
      isPeriodPermitted = evaluatePolicyPermission(
        policyCode,
        currentExpiryDate,
      );
    }

    const today = moment();
    if (
      isCostlyTab &&
      currentExpiryDate.isSameOrBefore(today.clone().add(6, "months"))
    ) {
      alert(
        "Οι επιστροφές που γίνονται αποδεκτές είναι όσες έχουν ελάχιστη περίοδο 6 μηνών πριν από την ημερομηνία λήξης.",
      );
      return;
    }

    setAllReturnProducts((prev) => [
      ...prev,
      {
        ...allProductKeys,
        periodPermitted: isPeriodPermitted,
        isExpired: currentExpiryDate
          ? currentExpiryDate.isBefore(today)
          : false,
      },
    ]);

    setSelectedProducts({});
    setSearchValue("");
    setSearchedNormalProduct(null);
    setSearchedOverdueProduct(null);
  };

  const handleDeleteItem = (index) => {
    setAllReturnProducts((prev) => {
      const updatedProducts = [...prev];
      updatedProducts.splice(index, 1);
      return updatedProducts;
    });
  };

  const handleAgreementChange = (index, checked) => {
    const updatedProducts = [...allReturnProducts];
    updatedProducts[index] = {
      ...updatedProducts[index],
      agreement: checked,
    };
    setAllReturnProducts(updatedProducts);
  };

  // ── Tab navigation ──────────────────────────────────────────────────────
  const handleDifferentForm = (type) => {
    if (allReturnProducts?.length === 0) {
      navigate(`/app/return-product?type=${type}`);
    } else if (window.confirm("Οι αλλαγές σας θα χαθούν. Είσαστε σίγουροι")) {
      setAllReturnProducts([]);
      navigate(`/app/return-product?type=${type}`);
    }
  };

  // ── Costly tab confirmation ──────────────────────────────────────────────
  const handleCostlyConfirm = () => {
    setCostlyConfirmOpen(false);
    if (costlyPendingProduct) {
      setSearchedOverdueProduct(costlyPendingProduct);
      setSearchedNormalProduct(null);
    }
    setCostlyPendingProduct(null);
  };

  const handleCostlyCancel = () => {
    setCostlyConfirmOpen(false);
    setCostlyPendingProduct(null);
  };

  console.log(allReturnProducts, "allReturnProducts");

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div>
      {/* <div className="pb-4 flex">
        <Button
          variant="outlined"
          onClick={() => handleDifferentForm("costly")}
        >
          ΕΠΙΣΤΡΟΦΗ ΦΑΡΜΑΚΩΝ ΑΝΩ ΤΩΝ €40
        </Button>
      </div> */}
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex flex-col justify-center">
        <Typography variant="h4" align="center" fontSize={25} gutterBottom>
          {TAB_LABELS[currentTab] || ""}
          <div className="tooltip-wrapper hidden sm:inline-block ml-1">
            <Info className="info-icon" />
            <div className="tooltip-content">
              <p>
                Κάθε αίτημα επιστροφής μπορεί να περιλαμβάνει έως 10 προϊόντα,
                λόγω περιορισμού χωρητικότητας του εκτυπώσιμου PDF. Για
                επιστροφή περισσότερων προϊόντων, απαιτείται η δημιουργία νέου
                αιτήματος επιστροφής.
              </p>
            </div>
          </div>
        </Typography>

        {showNavButtons && (
          <div className="flex gap-3 justify-center items-center">
            {/* {NAV_TABS.filter((tab) => tab !== currentTab).map((tab) => ( */}
            {NAV_TABS.map((tab) => (
              <Button
                key={tab}
                size="small"
                variant="contained"
                onClick={() => handleDifferentForm(tab)}
              >
                {NAV_TAB_LABELS[tab]}
              </Button>
            ))}
            <Button
              variant="contained"
              onClick={() => handleDifferentForm("costly")}
            >
              Φάρμακα άνω €40
            </Button>
            <Link to="/app/return-product-list">
              <Button variant="outlined" size="small">
                History
              </Button>
            </Link>
          </div>
        )}
        {isCostlyTab && (
          <p className="text-sm">
            <span className="font-semibold">⚠️Προϋποθέσεις Επιστροφής</span>
            <br />
            - Φάρμακα αξίας άνω των €40 χωρίς ΦΠΑ (χονδρική τιμή).
            <br />
            - Να έχουν αγοραστεί εντός των τελευταίων 3 μηνών.
            <br />
            - Να διαθέτουν ελάχιστη υπολειπόμενη διάρκεια ζωής 6 μηνών από την
            ημερομηνία λήξης.
            <br />
            - Να μην ανήκουν στην κατηγορία φαρμάκων ψυγείου.
            <br />
          </p>
        )}
        {isOthersTab && (
          <p className="text-sm">
            <span className="font-semibold">
              ⚠️Αφορά μόνο προϊόντα που είναι απενεργοποιημένα.
            </span>
          </p>
        )}
      </div>

      {/* ── Return Form Table ───────────────────────────────────────────── */}
      <div className="overflow-x-auto my-3">
        <table className="min-w-full bg-white border border-gray-300">
          <thead>
            <tr>
              {tableHeaders.map((header, index) => (
                <th key={index} className="p-3 border-gray-300">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {allReturnProducts.map((item, index) => (
              <tr key={index} className="border-b border-gray-300">
                <td className="p-3">
                  {item?.IV || "N/A"}
                  <br />
                  <Button color="error" onClick={() => handleDeleteItem(index)}>
                    Remove
                  </Button>
                </td>
                <td className="p-3">{item.PARTNAME}</td>
                <td className="p-3">{item.PARTDES || "-"}</td>
                <td className="p-3">
                  {dextPolicyObject[item.DEXT_CSPOLICYCODE] || "-"}
                </td>
                <td className="p-3">{item?.currentExpiry || "N/A"}</td>
                <td className="p-3">{item?.lotNo || "-"}</td>
                <td className="p-3">{item?.qty}</td>
                {!isCostlyTab && (
                  <td className="p-3">
                    {!item.periodPermitted ? (
                      <Checkbox
                        onChange={(e) =>
                          handleAgreementChange(index, e.target.checked)
                        }
                      />
                    ) : (
                      "N/A"
                    )}
                  </td>
                )}
                {isOthersTab && <td className="p-3">{item?.comment}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Product Search (hidden for "others" tab) ────────────────────── */}
      {!isOthersTab && (
        <div className="flex flex-col gap-3 my-3">
          <div style={{ width: 360 }}>
            <AsyncAutocomplete
              value={productData}
              onChange={setProductData}
              label="Kedifap Code/PARTNAME/BARCODE"
              placeholder="Search for parts..."
              debounceMs={350}
              minSearchLength={2}
              getOptionLabel={(o) => o.PARTNAME}
            />
          </div>

          <div className="flex gap-3">
            <Button
              variant="contained"
              onClick={() => fetchReturnPolicy(currentTab)}
              disabled={!productData}
            >
              SUBMIT
            </Button>
          </div>

          {isLoadingPolicy && <div className="py-1">Loading...</div>}

          {searchedOverdueProduct && (
            <div className="flex flex-col gap-2">
              <p>{searchedOverdueProduct.PARTDES}</p>
              {isCostlyTab && (
                <div className="text-sm text-gray-600">
                  <p>
                    Χονδρική τιμή (χωρίς ΦΠΑ): €
                    {Number(searchedOverdueProduct.WSPLPRICE || 0).toFixed(2)}
                  </p>
                  <p>
                    Ψυγείο:{" "}
                    {searchedOverdueProduct.DEXT_FRIDGE === "1" ? "Ναι" : "Όχι"}
                  </p>
                  <p>Pharma Code: {searchedOverdueProduct.SPEC14 || "—"}</p>
                </div>
              )}
            </div>
          )}

          {searchedNormalProduct && (
            <div className="flex flex-col gap-2">
              <p>{searchedNormalProduct.PARTDES}</p>
            </div>
          )}

          {(searchedNormalProduct || searchedOverdueProduct) &&
            isOverDueTab && (
              <div className="flex flex-col gap-2">
                <p>
                  {dextPolicyObject[searchedOverdueProduct.DEXT_CSPOLICYCODE]}
                </p>
              </div>
            )}
        </div>
      )}

      {/* ── Add Product Section ─────────────────────────────────────────── */}
      {showAddSection && (
        <div className="p-3 rounded-md flex flex-col justify-between items-center bg-slate-100">
          <Typography fontWeight={700}>Add Product in Return Form:</Typography>

          {isFetchingInvoice && (
            <div className="flex items-center w-full gap-2 mt-3">
              Loading...
            </div>
          )}

          {/* ── Normal tab: invoice-based inputs ──────────────────────── */}
          {!isFetchingInvoice && searchedNormalProduct && !isOthersTab && (
            <div className="w-full">
              {allInvoices && allInvoices?.length > 0 ? (
                <div className="flex items-center w-full gap-2 mt-3">
                  {/* NOTE: This Autocomplete is unreachable in the original code
                      (outer check requires length > 0, inner requires length === 0).
                      Preserved exactly to avoid breaking anything. */}
                  {searchedNormalProduct && allInvoices?.length === 0 && (
                    <Autocomplete
                      size="small"
                      disablePortal
                      options={allInvoices}
                      getOptionLabel={(option) => option.IV || ""}
                      value={selectedProducts || null}
                      onChange={(event, newValue) =>
                        setSelectedProducts(newValue || {})
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Invoice"
                          sx={{ minWidth: 200 }}
                        />
                      )}
                    />
                  )}
                  {searchedNormalProduct &&
                    allInvoices &&
                    allInvoices?.length > 0 &&
                    !isNormalTab && (
                      <TextField
                        size="small"
                        placeholder="Invoice no."
                        value={selectedProducts.IV || ""}
                        onChange={(e) =>
                          setSelectedProducts((prev) => ({
                            ...prev,
                            IV: e.target.value,
                          }))
                        }
                      />
                    )}
                  <TextField
                    size="small"
                    type="number"
                    placeholder="Qty"
                    value={selectedProducts.qty || ""}
                    onChange={(e) =>
                      setSelectedProducts((prev) => ({
                        ...prev,
                        qty: +e.target.value,
                      }))
                    }
                  />
                  <TextField
                    size="small"
                    placeholder="Lot no."
                    value={selectedProducts.lotNo || ""}
                    onChange={(e) =>
                      setSelectedProducts((prev) => ({
                        ...prev,
                        lotNo: e.target.value,
                      }))
                    }
                  />
                  <TextField
                    size="small"
                    type="date"
                    placeholder="Expiry date"
                    value={selectedProducts.currentExpiry || ""}
                    onChange={(e) =>
                      setSelectedProducts((prev) => ({
                        ...prev,
                        currentExpiry: e.target.value,
                      }))
                    }
                  />
                  <Button variant="contained" onClick={handleAddItem}>
                    Add
                  </Button>
                </div>
              ) : (
                <p>Not found any invoice</p>
              )}
            </div>
          )}

          {/* ── Overdue (non-recall) + Recall: same inputs, unified ───── */}
          {/* Original had two identical blocks split by currentTab === "recall".
              Merged into one since the rendered fields are identical. */}
          {!isFetchingInvoice && searchedOverdueProduct && (
            <div className="w-full">
              <ProductInputFields
                selectedProducts={selectedProducts}
                setSelectedProducts={setSelectedProducts}
                onAdd={handleAddItem}
              />
            </div>
          )}

          {/* ── Others tab: manual entry ──────────────────────────────── */}
          {isOthersTab && (
            <div className="w-full">
              <div className="flex items-center w-full gap-2 mt-3">
                <TextField
                  size="small"
                  placeholder="PARTNAME"
                  value={selectedProducts.PARTNAME || ""}
                  onChange={(e) =>
                    setSelectedProducts((prev) => ({
                      ...prev,
                      PARTNAME: e.target.value,
                    }))
                  }
                />
                <TextField
                  size="small"
                  type="number"
                  placeholder="Qty"
                  value={selectedProducts.qty || ""}
                  onChange={(e) =>
                    setSelectedProducts((prev) => ({
                      ...prev,
                      qty: +e.target.value,
                    }))
                  }
                />
                <TextField
                  size="small"
                  type="date"
                  placeholder="Expiry date"
                  value={selectedProducts.currentExpiry || ""}
                  onChange={(e) =>
                    setSelectedProducts((prev) => ({
                      ...prev,
                      currentExpiry: e.target.value,
                    }))
                  }
                />
                <TextField
                  size="small"
                  placeholder="Comment"
                  value={selectedProducts?.comment || ""}
                  onChange={(e) =>
                    setSelectedProducts((prev) => ({
                      ...prev,
                      comment: e.target.value,
                    }))
                  }
                />
                <Button variant="contained" onClick={handleAddItem}>
                  Add
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Submit Button ───────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3 items-center mt-4">
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmitForm}
          disabled={allReturnProducts?.length === 0}
        >
          Submit
        </Button>
      </div>

      {/* ── Costly tab: purchase date confirmation dialog ────────────── */}
      <Dialog open={costlyConfirmOpen} onClose={handleCostlyCancel}>
        <DialogTitle>Επιβεβαίωση αγοράς</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Επιβεβαιώνετε ότι το προϊόν{" "}
            <strong>
              {costlyPendingProduct?.PARTDES || costlyPendingProduct?.PARTNAME}
            </strong>{" "}
            αγοράστηκε εντός των τελευταίων 3 μηνών;
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCostlyCancel} color="inherit">
            Όχι
          </Button>
          <Button onClick={handleCostlyConfirm} variant="contained">
            Ναι, επιβεβαιώνω
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ReturnProduct;
